import cuid from 'cuid'
import {
  lastDayOfMonth, addMonths, parseISO, startOfDay, isValid
} from 'date-fns'

export const getInitials = (name = ''): string => name
  .replace(/\s+/, ' ')
  .split(' ')
  .slice(0, 2)
  .map((v) => v && v[0].toUpperCase())
  .join('')

type TrucateOptions = { length?: number, omission?: string }
export const truncate = (str: string, options?: TrucateOptions) => {
  const length = options?.length || 30
  const omission = options?.omission || '...'
  return str.length > length ? `${str.substring(0, length)}${omission}` : str
}

export const getFilePathForAzureUpload = (tableName: 'bill' | 'deposit' | 'account', accountId: string, fileName: string): string => `${accountId}/${tableName}s/${cuid()}-${fileName}`
export const validateEmail = (email) => {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const syntheticChangeEvent = (handleChange, targetName, targetValue) => {
  const event = new Event('input', { bubbles: true });
  handleChange({ ...event, target: { ...event.target, name: targetName, value: targetValue } })
}

export const touchAllFields = (formState) => ({
  ...formState.touched,
  ...Object.assign({}, ...Object.keys(formState.values).map((item) => ({ [item]: true })))
})

export const formatPhoneOnInput = (phoneNumber: string) => {
  const input = phoneNumber ? phoneNumber.trim().replace(/\D/g, '') : '';
  const size = input.length
  if (size > 6) { return `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6)}` }
  if (size > 3) { return `(${input.slice(0, 3)}) ${input.slice(3)}` }
  if (size > 0) { return `(${input}` }
  return input
}

export const formatSSNOnInput = (ssn) => {
  const input = ssn ? ssn.replace(/\D/g, '') : '';
  const size = input.length
  if (size > 5) { return `${input.slice(0, 3)}-${input.slice(3, 5)}-${input.slice(5)}` }
  if (size > 3) { return `${input.slice(0, 3)}-${input.slice(3)}` }
  if (size > 0) { return `${input}` }
  return input
}

export const getNextDueDate = (dayOfMonth: number, start_date?: string, lastTransaction?: any): Date => {
  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const constructDate = (year: number, month: number, day: number): Date => {
    const tempDate = new Date(year, month, 1);
    const maxDay = lastDayOfMonth(tempDate).getDate();
    const validDay = Math.min(day || 1, maxDay);
    return new Date(year, month, validDay);
  };

  const isProcessed = lastTransaction?.status === 'Processed' || !!lastTransaction?.processed_completed_date;

  if (isProcessed) {
    const rawLastDate = lastTransaction?.due_date || lastTransaction?.date || lastTransaction?.processing_date;
    if (rawLastDate) {
      const parsedLast = typeof rawLastDate === 'string' ? parseISO(rawLastDate) : rawLastDate;
      if (isValid(parsedLast)) {
        const nextMonthAfterLast = addMonths(parsedLast, 1);
        const candidateDate = constructDate(nextMonthAfterLast.getFullYear(), nextMonthAfterLast.getMonth(), dayOfMonth);

        if (startOfDay(candidateDate) < startOfDay(today)) {
          if (dayOfMonth >= todayDay) {
            return constructDate(todayYear, todayMonth, dayOfMonth);
          }
          const nextM = addMonths(today, 1);
          return constructDate(nextM.getFullYear(), nextM.getMonth(), dayOfMonth);
        }
        return candidateDate;
      }
    }
  }

  if (start_date) {
    const parsedStart = typeof start_date === 'string' ? parseISO(start_date) : start_date;
    if (isValid(parsedStart) && startOfDay(parsedStart) > startOfDay(today)) {
      const startDay = parsedStart.getDate();
      const startMonth = parsedStart.getMonth();
      const startYear = parsedStart.getFullYear();

      if (dayOfMonth >= startDay) {
        return constructDate(startYear, startMonth, dayOfMonth);
      }
      const nextMonth = addMonths(parsedStart, 1);
      return constructDate(nextMonth.getFullYear(), nextMonth.getMonth(), dayOfMonth);
    }
  }

  if (dayOfMonth >= todayDay) {
    return constructDate(todayYear, todayMonth, dayOfMonth);
  }
  const nextM = addMonths(today, 1);
  return constructDate(nextM.getFullYear(), nextM.getMonth(), dayOfMonth);
};
